import React, { useState, useEffect } from 'react';
import {
  ScatterChart, Scatter, XAxis, YAxis, CartesianGrid,
  Tooltip, Legend, ResponsiveContainer, ZAxis
} from 'recharts';
import axios from 'axios';
import exportPDF from '../utils/exportPDF.js';
import { apiPath } from "../apiurl.js";
import LoadingSequence from "../assets/Loading/LoadingSequence";
import { UserGuideScatterPlot } from '../utils/UserGuide';
import Pagination from '../utils/Pagination';
import { useHistory } from 'react-router-dom';
import '../assets/css/ScatterPlot.css';

function ScatterPlot() {
  const defaultRunId = 'SRR9686066';
  const [run, setRun] = useState(defaultRunId);
  const [selectedDropdown, setSelectedDropdown] = useState('readHit');
  const [apiData, setApiData] = useState(null);
  const [scatterData, setScatterData] = useState({
    poliovirus: [],
    likelyPoliovirus: [],
    nonPoliovirus: [],
    contighitsdata: []
  });
  const [totals, setTotals] = useState({ poliovirus: 0, LikelyPoliovirus: 0, nonPoliovirus: 0, contighitsdata: 0 });
  const [disclaimer, setDisclaimer] = useState('');
  const [noData, setNoData] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [userGuideOpen, setUserGuideOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage] = useState(8);
  const [filterModel, setFilterModel] = useState({ items: [] });
  const history = useHistory();

  const handleOpenUserGuide = () => setUserGuideOpen(true);
  const handleCloseUserGuide = () => setUserGuideOpen(false);

  useEffect(() => {
    searchApi(); // eslint-disable-line
  }, [run, selectedDropdown]);

  const handleInputChange = (event) => {
    setRun(event.target.value);
  };

  const handleDropdownChange = (key) => {
    setSelectedDropdown(key);
    setApiData(null);
    setScatterData({
      poliovirus: [],
      likelyPoliovirus: [],
      nonPoliovirus: [],
      contighitsdata: []
    });
    setCurrentPage(1);
  };

  const formatDataForRecharts = (data) => {
    if (!data || !data.scatterPlotData) return {
      poliovirus: [],
      likelyPoliovirus: [],
      nonPoliovirus: [],
      contighitsdata: []
    };

    const result = {};

    if (data.scatterPlotData.poliovirus) {
      result.poliovirus = data.scatterPlotData.poliovirus.map(item => ({ x: item.x, y: item.y }));
    }
    if (data.scatterPlotData.LikelyPoliovirus) {
      result.likelyPoliovirus = data.scatterPlotData.LikelyPoliovirus.map(item => ({ x: item.x, y: item.y }));
    }
    if (data.scatterPlotData.nonPoliovirus) {
      result.nonPoliovirus = data.scatterPlotData.nonPoliovirus.map(item => ({ x: item.x, y: item.y }));
    }
    if (data.scatterPlotData.contighitsdata) {
      result.contighitsdata = data.scatterPlotData.contighitsdata.map(item => ({ x: item.x, y: item.y }));
    }

    return result;
  };

  const searchApi = () => {
    setIsLoading(true);
    setNoData(false);

    let url = '';
    if (run) {
      if (selectedDropdown === 'readHit') {
        url = `${apiPath}/api/ScatterPlotreadhit/${run}`;
      } else if (selectedDropdown === 'contighits') {
        url = `${apiPath}/api/ScatterPlotcontighits/${run}`;
      }

      if (url) {
        axios.get(url)
          .then(response => {
            setIsLoading(false);
            if (!response.data || (response.data.scatterPlotData && Object.keys(response.data.scatterPlotData).length === 0)) {
              setNoData(true);
              setScatterData({ poliovirus: [], likelyPoliovirus: [], nonPoliovirus: [], contighitsdata: [] });
            } else {
              setApiData(response.data);
              const formattedData = formatDataForRecharts(response.data);
              setScatterData(formattedData);
              setDisclaimer(response.data.disclaimer);
              setTotals(response.data.totals || { poliovirus: 0, LikelyPoliovirus: 0, nonPoliovirus: 0, contighitsdata: 0 });
            }
          })
          .catch(error => {
            console.error('Error fetching data:', error);
            setIsLoading(false);
            setNoData(true);
          });
      }
    }
  };

  const clearFilters = () => {
    setRun('');
    setSelectedDropdown('');
    setApiData(null);
    setScatterData({ poliovirus: [], likelyPoliovirus: [], nonPoliovirus: [], contighitsdata: [] });
    setTotals({ poliovirus: 0, LikelyPoliovirus: 0, nonPoliovirus: 0, contighitsdata: 0 });
    setDisclaimer('');
    setNoData(false);
    setIsLoading(false);
    window.location.reload();
  };

  const getEndpoint = (key) => {
    switch (key) {
      case "contighits": return "ContigHits";
      case "readHit":    return "ReadHits";
      default:           return null;
    }
  };

  const handleRouteClick = (key) => {
    const endpoint = getEndpoint(key);
    if (endpoint) {
      const filterModelParam = encodeURIComponent(JSON.stringify(filterModel));
      history.push(`/pass/${endpoint}?run=${run}&filters=${filterModelParam}`);
    }
  };

  // Pagination
  const indexOfLastItem  = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = apiData ? Object.entries(apiData.pidentCounts).slice(indexOfFirstItem, indexOfLastItem) : [];
  const paginate = (pageNumber) => setCurrentPage(pageNumber);

  // Custom tooltip
  const CustomTooltip = ({ active, payload }) => {
    if (active && payload && payload.length) {
      return (
        <div className="sp-tooltip">
          <div className="sp-tooltip-row">
            <span className="sp-tooltip-key">Percent Identity</span>
            <span className="sp-tooltip-val">{payload[0].payload.x}</span>
          </div>
          <div className="sp-tooltip-row">
            <span className="sp-tooltip-key">Log Length</span>
            <span className="sp-tooltip-val">{payload[0].payload.y}</span>
          </div>
        </div>
      );
    }
    return null;
  };

  const renderScatterPlot = () => {
    const hasData = (
      scatterData.poliovirus?.length > 0 ||
      scatterData.likelyPoliovirus?.length > 0 ||
      scatterData.nonPoliovirus?.length > 0 ||
      scatterData.contighitsdata?.length > 0
    );

    if (!hasData) {
      return (
        <div className="sp-no-data">
          <div className="sp-no-data-icon">⊹</div>
          <div className="sp-no-data-label">No Data Available</div>
          <div className="sp-no-data-sub">Enter a run ID and press Search</div>
        </div>
      );
    }

    return (
      <ResponsiveContainer width="100%" height="100%">
        <ScatterChart margin={{ top: 20, right: 20, bottom: 70, left: 30 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
          <XAxis
            type="number"
            dataKey="x"
            name="Percent Identity"
            tick={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }}
            label={{
              value: 'Percent Identity (pident)',
              position: 'bottom',
              offset: 20,
              style: { textAnchor: 'middle', fontFamily: "'Inter', sans-serif", fontSize: 12, fill: '#475569' }
            }}
          />
          <YAxis
            type="number"
            dataKey="y"
            name="Alignment Length (Log 10)"
            tick={{ fontFamily: "'JetBrains Mono', monospace", fontSize: 11 }}
            label={{
              value: 'Alignment Length (Log 10)',
              angle: -90,
              position: 'left',
              style: { textAnchor: 'middle', fontFamily: "'Inter', sans-serif", fontSize: 12, fill: '#475569' }
            }}
          />
          <ZAxis range={[20, 20]} />
          <Tooltip content={<CustomTooltip />} />
          <Legend
            verticalAlign="top"
            height={50}
            wrapperStyle={{ paddingTop: '10px', fontFamily: "'Inter', sans-serif", fontSize: 12 }}
          />

          {selectedDropdown === 'readHit' ? (
            <>
              {scatterData.poliovirus?.length > 0 && (
                <Scatter name="Poliovirus" data={scatterData.poliovirus} fill="#dc2626" shape="circle" />
              )}
              {scatterData.likelyPoliovirus?.length > 0 && (
                <Scatter name="Likely Poliovirus" data={scatterData.likelyPoliovirus} fill="#0d9488" shape="circle" />
              )}
              {scatterData.nonPoliovirus?.length > 0 && (
                <Scatter name="Non-poliovirus" data={scatterData.nonPoliovirus} fill="#4f46e5" shape="circle" />
              )}
            </>
          ) : (
            <>
              {scatterData.contighitsdata?.length > 0 && (
                <Scatter name="Contig Hits Data" data={scatterData.contighitsdata} fill="#059669" shape="circle" />
              )}
            </>
          )}
        </ScatterChart>
      </ResponsiveContainer>
    );
  };

  return (
    <>
      <div className="sp-root">

        {/* ── Command bar ── */}
        <div className="sp-topbar">
          <span className="sp-topbar-hint">Run: SRR9686066 · Mode: Read Hit / Contig Hits</span>

          <input
            type="text"
            className="sp-run-input"
            placeholder={`Run ID (default: ${defaultRunId})`}
            value={run}
            onChange={handleInputChange}
          />

          <div className="sp-mode-toggle">
            <button
              className={`sp-mode-btn${selectedDropdown === 'readHit' ? ' active' : ''}`}
              onClick={() => handleDropdownChange('readHit')}
            >
              Read Hit
            </button>
            <button
              className={`sp-mode-btn${selectedDropdown === 'contighits' ? ' active' : ''}`}
              onClick={() => handleDropdownChange('contighits')}
            >
              Contig Hits
            </button>
          </div>

          <button className="sp-btn sp-btn-search" onClick={searchApi}>Search</button>
          <button className="sp-btn sp-btn-clear" onClick={clearFilters}>Clear</button>
          <button className="sp-btn sp-btn-export" onClick={exportPDF}>Export PDF</button>
        </div>

        {/* ── Body ── */}
        <div className="sp-body">

          {/* Chart */}
          <div className="sp-chart-area">
            <div className="sp-chart-canvas">
              {isLoading ? (
                <LoadingSequence message="Loading scatter data" size="medium" />
              ) : (
                renderScatterPlot()
              )}
            </div>
          </div>

          {/* Stats panel */}
          <div className="sp-panel">
            <div className="sp-panel-header">
              <div className="sp-panel-title">
                {selectedDropdown === 'readHit' ? 'Read Hit Summary' : 'Contig Hits Summary'}
              </div>
            </div>

            {/* Totals */}
            <div className="sp-stat-grid">
              {selectedDropdown === 'readHit' ? (
                <>
                  <div className="sp-stat-card">
                    <span className="sp-stat-label">
                      <span className="sp-stat-dot poliovirus" />
                      Poliovirus
                    </span>
                    <span className="sp-stat-value">{totals.poliovirus}</span>
                  </div>
                  <div className="sp-stat-card">
                    <span className="sp-stat-label">
                      <span className="sp-stat-dot likely" />
                      Likely Poliovirus
                    </span>
                    <span className="sp-stat-value">{totals.LikelyPoliovirus}</span>
                  </div>
                  <div className="sp-stat-card">
                    <span className="sp-stat-label">
                      <span className="sp-stat-dot non" />
                      Non-poliovirus
                    </span>
                    <span className="sp-stat-value">{totals.nonPoliovirus}</span>
                  </div>
                </>
              ) : (
                <div className="sp-stat-card">
                  <span className="sp-stat-label">
                    <span className="sp-stat-dot contig" />
                    Contig Hits
                  </span>
                  <span className="sp-stat-value">{totals.contighitsdata}</span>
                </div>
              )}
            </div>

            {/* pident table */}
            <div className="sp-table-wrap">
              <table className="sp-table">
                <thead>
                  <tr>
                    <th>pident</th>
                    <th>Avg Log Len</th>
                    <th>Count</th>
                  </tr>
                </thead>
                <tbody>
                  {currentItems.map(([pident, details]) => (
                    <tr key={pident}>
                      <td>{pident}</td>
                      <td>{details.avgLength.toFixed(5)}</td>
                      <td>{details.count}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="sp-pagination-wrap">
              <Pagination
                totalItems={apiData ? Object.entries(apiData.pidentCounts).length : 0}
                itemsPerPage={itemsPerPage}
                currentPage={currentPage}
                onPageChange={paginate}
              />
            </div>
          </div>
        </div>

        {/* ── Footer ── */}
        <div className="sp-footer">
          <div className="sp-footer-left">
            <button className="sp-btn-guide" onClick={handleOpenUserGuide}>User Guide</button>
          </div>

          <div className="sp-footer-center">
            {disclaimer && (
              <div className="sp-disclaimer">
                <span className="sp-disclaimer-icon">⚠</span>
                <span><strong>Disclaimer:</strong> {disclaimer}</span>
              </div>
            )}
          </div>

          <div className="sp-footer-right">
            <button className="sp-btn-navigate" onClick={() => handleRouteClick(selectedDropdown)}>
              Go to <span className="sp-nav-run">{run}</span> Data Table →
            </button>
          </div>
        </div>
      </div>

      <UserGuideScatterPlot open={userGuideOpen} onClose={handleCloseUserGuide} />
    </>
  );
}

export default ScatterPlot;
